import { useAuth } from '#layers/access/stores/auth';
import { redirect } from '#layers/access/utils/redirect';

/**
 * Ends the session at a hard document boundary.
 *
 * A reload is the only eraser that cannot miss: rstore rows, Pinia setup stores
 * (which have no `$reset`), module singletons and closures all die with the
 * document, so nothing the previous user loaded survives into the next session
 * in this tab. Purging by hand would mean maintaining a list that silently rots
 * every time a store is added.
 *
 * `target` is a full relative path so the caller's intent survives — the invite
 * and password-reset flows carry their token in the query string, and reloading
 * the same URL hands it straight back to a logged-out document.
 */
export function useSessionEnd() {
	const auth = useAuth();

	async function logoutAndReload(target: string) {
		try {
			await auth.logout();
		}
		catch (error) {
			// The engine rejects logout once another tab has dropped the shared
			// refresh cookie — the session is already gone server-side. Reload
			// anyway rather than stranding this tab with the previous user's
			// caches behind a signed-out shell.
			console.warn('[useSessionEnd] logout request failed; ending the session anyway', error);
		}

		redirect(target, { replace: true });
	}

	return { logoutAndReload };
}
